import React, { useEffect, useRef, useState } from 'react';
import { t } from 'i18n';

import Icon from 'Components/Base/Icon';

const i18nOpts = { scope: 'place_contacts.admin_panel.form' };

const AllActionPlansCheckbox = ({ checked, someSelected }) => {
  // I think this is the cleanest most readable way

  if (someSelected) {
    return (
      <Icon
        name="box-partial"
        className="checkbox checkbox--partial"
      />
    );
  }

  if (checked) {
    return (
      <Icon
        name="box-checked"
        className="checkbox checkbox--checked"
      />
    );
  }

  return (
    <Icon
      name="box-empty"
      className="checkbox checkbox--unchecked"
    />
  );
};

const CarePlanTemplatesSelect = ({
  carePlanTemplates,
  selectedIds,
  setSelectedIds,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredTemplates = carePlanTemplates.filter((template) =>
    template.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const allCarePlanTemplatesCount = carePlanTemplates.length;
  const selectedCount = selectedIds.length;
  const allSelected = allCarePlanTemplatesCount > 0 && selectedCount === allCarePlanTemplatesCount;
  const someSelected = selectedCount > 0 && !allSelected;

  const toggleAll = () => {
    if (allSelected) {
      // reset to none selected
      setSelectedIds([]);
    } else {
      setSelectedIds(carePlanTemplates.map((template) => template.id));
    }
  };

  const toggleOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // looks a bit weird, but we need this
  const openDropdown = () => {
    setIsOpen(true);
    // defer focus to ensure the input is mounted and not stolen by another handler
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  };

  const toggleDropdown = () => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      openDropdown();
    }
  };

  return (
    <div className="care_plan_templates_select" ref={containerRef}>
      <div className="field_wrapper">
        <div
          className={isOpen ? 'field field--open' : 'field'}
          onClick={() => !isOpen && openDropdown()}
        >
          <input
            ref={inputRef}
            type="text"
            className="search_input"
            placeholder={t('search_placeholder', i18nOpts)}
            value={searchQuery}
            onChange={({ target: { value } }) => setSearchQuery(value)}
            onFocus={() => setIsOpen(true)}
          />
          <span
            className="caret_icon"
            onClick={(event) => {
              event.stopPropagation();
              toggleDropdown();
            }}
            aria-hidden="true"
          >
            <Icon name="arrow-triangle-down" className="caret" />
          </span>
        </div>

        {isOpen && (
          <ul className="templates_dropdown" role="listbox">
            <li
              className="option option--all"
              onClick={toggleAll}
              role="option"
              aria-selected={allSelected}
            >
              <AllActionPlansCheckbox checked={allSelected} someSelected={someSelected} />
              <span className="option_label">
                {t('all_action_plan_templates', i18nOpts)}
              </span>
            </li>
            {filteredTemplates.map((template) => {
              const checked = selectedIds.includes(template.id);
              return (
                <li
                  key={template.id}
                  className="option"
                  onClick={() => toggleOne(template.id)}
                  role="option"
                  aria-selected={checked}
                >
                  <Icon
                    name={checked ? 'box-checked' : 'box-empty'}
                    className={checked ? 'checkbox checkbox--checked' : 'checkbox checkbox--unchecked'}
                  />
                  <span className="option_label">{template.name}</span>
                </li>
              );
            })}
            {filteredTemplates.length === 0 && (
              <li className="no_results">
                {t('no_templates_found', i18nOpts)}
              </li>
            )}
          </ul>
        )}
      </div>

      <p className="hint">{t('care_plan_templates_hint', i18nOpts)}</p>
    </div>
  );
};

export default CarePlanTemplatesSelect;
